import type { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import type { PermissionService } from "../PermissionService";
import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { ContextVoter } from "./composables/ContextVoter";

export type ScopedViewVoteItem = Omit<CrudArgs, "@id"> & {
  relatedUser?: string | null
  permissionTemplateGroup?: string | null
  ptgClient?: string | null
  ptgLocation?: string | null
};

export interface ScopedViewPermissionAttributes {
  readAny: PermissionAttributesEnum
  createAny: PermissionAttributesEnum
  updateAny: PermissionAttributesEnum
  deleteAny: PermissionAttributesEnum
  read: PermissionAttributesEnum
  create: PermissionAttributesEnum
  update: PermissionAttributesEnum
  delete: PermissionAttributesEnum
  readCollectionViaClient: PermissionAttributesEnum
  readCollectionViaLocation: PermissionAttributesEnum
  readCollectionViaPtg: PermissionAttributesEnum
  readCollectionGlobal: PermissionAttributesEnum
  readEffective: PermissionAttributesEnum
  readDefinitionSchema: PermissionAttributesEnum
}

/**
 * Shared voter for TaskView / ReportView: same scope rules as UserPreferenceVoter,
 * plus global collection and definition-schema reads.
 */
export abstract class AbstractScopedViewVoter extends ContextVoter {
  public permissionService: PermissionService;

  protected abstract readonly attrs: ScopedViewPermissionAttributes;

  constructor(permissionService: PermissionService) {
    super(permissionService);
    this.permissionService = permissionService;
  }

  private hasContextScopedAccess(
    item: ScopedViewVoteItem,
    contextAttribute: PermissionAttributesEnum,
  ): boolean {
    if ((item.client || item.location) && this.hasAccess({
      client: item.client,
      location: item.location,
      attribute: contextAttribute,
    })) {
      return true;
    }

    if (!item.permissionTemplateGroup) {
      return false;
    }

    if (!item.ptgClient && !item.ptgLocation) {
      return false;
    }

    return this.hasAccess({
      client: item.ptgClient,
      location: item.ptgLocation,
      attribute: contextAttribute,
    });
  }

  private isOwnUserRow(item?: ScopedViewVoteItem): boolean {
    if (!item?.relatedUser || !this.permissionService.userIri) {
      return false;
    }

    return item.relatedUser === this.permissionService.userIri;
  }

  public canReadAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: this.attrs.readAny,
    });
  };

  public canCreateAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: this.attrs.createAny,
    });
  };

  public canUpdateAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: this.attrs.updateAny,
    });
  };

  public canDeleteAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: this.attrs.deleteAny,
    });
  };

  public canReadCollectionViaClient = (item: ScopedViewVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }
    return this.hasContextScopedAccess(item, this.attrs.readCollectionViaClient);
  };

  public canReadCollectionViaLocation = (item: ScopedViewVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }
    return this.hasContextScopedAccess(item, this.attrs.readCollectionViaLocation);
  };

  public canReadCollectionViaPtg = (item: ScopedViewVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }
    return this.hasContextScopedAccess(item, this.attrs.readCollectionViaPtg);
  };

  public canReadGlobalCollection = () => {
    return this.canReadAny();
  };

  public canReadEffective = (item: ScopedViewVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }
    return this.hasContextScopedAccess(item, this.attrs.readEffective);
  };

  public canReadDefinitionSchema = () => {
    return true;
  };

  public canCreate = (voteItem?: ScopedViewVoteItem): boolean => {
    if (this.canCreateAny()) {
      return true;
    }

    if (voteItem?.relatedUser) {
      return this.isOwnUserRow(voteItem);
    }

    if (!voteItem?.client && !voteItem?.location && !voteItem?.permissionTemplateGroup) {
      return false;
    }

    return this.hasContextScopedAccess(
      voteItem ?? {},
      this.attrs.create,
    );
  };

  public canUpdate: ContextVoterInterface<ScopedViewVoteItem>["canUpdate"] = (voteItem = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    if (this.canUpdateAny()) {
      return true;
    }

    if (voteItem.relatedUser) {
      return this.isOwnUserRow(voteItem);
    }

    if (!voteItem.client && !voteItem.location && !voteItem.permissionTemplateGroup) {
      return false;
    }

    return this.hasContextScopedAccess(voteItem, this.attrs.update);
  };

  public canDelete: ContextVoterInterface<ScopedViewVoteItem>["canDelete"] = (voteItem = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    if (this.canDeleteAny()) {
      return true;
    }

    if (voteItem.relatedUser) {
      return this.isOwnUserRow(voteItem);
    }

    if (!voteItem.client && !voteItem.location && !voteItem.permissionTemplateGroup) {
      return false;
    }

    return this.hasContextScopedAccess(voteItem, this.attrs.delete);
  };

  public canManage: AggregateVoterCheck<ScopedViewVoteItem> = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.canCreate(item)
      || this.canUpdate(item)
      || this.canDelete(item);
  };

  public canManageGlobal = () => {
    return this.canCreateAny() || this.canUpdateAny() || this.canDeleteAny();
  };

  public canManageForUser = (userIri: string | null | undefined) => {
    if (!userIri) {
      return false;
    }

    if (this.permissionService.userIri === userIri) {
      return true;
    }

    return this.canCreateAny() || this.canUpdateAny() || this.canDeleteAny();
  };
}
